import { organizationApi } from 'src/api/organizationApi';
import { COMPLIANCE_FRAMEWORK_FILTER_OPTIONS } from 'src/mindtime/pages/security/edr/configuration-compliance/complianceFrameworkOptions';

/** BE field name (typo preserved). */
export const SECURITY_COMPLIANCE_FRAMEWORKS_FIELD = 'securityComplinaceFrameworks';

export const COMPLIANCE_SETTINGS_FRAMEWORK_OPTIONS = COMPLIANCE_FRAMEWORK_FILTER_OPTIONS;

export type ComplianceFrameworkOrg = {
  id: string;
  name: string;
  frameworks: string[];
};

export type ComplianceFrameworkPatchEntry = {
  organizationId: string;
  frameworks: string[];
};

export const readOrgFrameworks = (org: Record<string, unknown> | null | undefined): string[] => {
  if (!org) return [];
  const raw = org[SECURITY_COMPLIANCE_FRAMEWORKS_FIELD];
  if (!Array.isArray(raw)) return [];
  return raw.map(String).filter(Boolean);
};

export const getOrgIdFromRecord = (org: Record<string, unknown>): string =>
  String(org.id ?? org._id ?? '').trim();

export const getOrgNameFromRecord = (org: Record<string, unknown>): string =>
  String(org.name ?? '').trim() || '—';

const unwrapData = (response: unknown): unknown => {
  const root = response as { data?: unknown } | null;
  const first = root?.data;
  if (first && typeof first === 'object' && !Array.isArray(first) && 'data' in (first as object)) {
    return (first as { data?: unknown }).data;
  }
  return first;
};

const mapFrameworkOrg = (item: Record<string, unknown>): ComplianceFrameworkOrg | null => {
  const id = getOrgIdFromRecord(item);
  if (!id) return null;
  return {
    id,
    name: getOrgNameFromRecord(item),
    frameworks: readOrgFrameworks(item),
  };
};

/** Parse GET /organization/{id}/frameworks (object or enveloped). */
export const parseFrameworksByOrgResponse = (response: unknown): ComplianceFrameworkOrg | null => {
  const payload = unwrapData(response);
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  return mapFrameworkOrg(payload as Record<string, unknown>);
};

/** Parse GET /organization/reseller/{id}/frameworks (array or enveloped). */
export const parseFrameworksByResellerResponse = (response: unknown): ComplianceFrameworkOrg[] => {
  const payload = unwrapData(response);
  const list = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];

  return list
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map(mapFrameworkOrg)
    .filter((row): row is ComplianceFrameworkOrg => row != null);
};

export const fetchFrameworksByOrganization = async (
  orgId: string,
): Promise<ComplianceFrameworkOrg | null> => {
  const response = await organizationApi.getFrameworksById(orgId);
  return parseFrameworksByOrgResponse(response);
};

export const fetchFrameworksByReseller = async (
  resellerId: string,
): Promise<ComplianceFrameworkOrg[]> => {
  const response = await organizationApi.getFrameworksByResellerId(resellerId);
  return parseFrameworksByResellerResponse(response);
};

/** Single PATCH for one or many orgs (client = 1 entry, reseller = dirty entries). */
export const patchOrganizationFrameworks = async (
  entries: ComplianceFrameworkPatchEntry[],
): Promise<void> => {
  if (!entries.length) return;
  await organizationApi.patchFrameworks({
    organizations: entries.map((entry) => ({
      organizationId: entry.organizationId,
      securityComplinaceFrameworks: entry.frameworks,
    })),
  });
};
